import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import SearchIcon from '@mui/icons-material/Search';
import {
  api,
  type DistrictOption,
  type MasterToolSearchFilters,
  type ProjectCategoryOption,
} from '../../services/api';
import {
  formatOnemapLocationLabel,
  parseOnemapCoordinates,
  searchOnemapLocations,
  type OneMapSearchResult,
} from '../../utils/onemapLocationSearch';

const EXCLUDED_CATEGORIES = new Set(['commercial', 'industrial', 'condotel']);

const glassPanelSx = {
  bgcolor: 'rgba(10, 20, 34, 0.86)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(34, 211, 238, 0.28)',
  borderRadius: '12px',
  boxShadow: '0 12px 40px rgba(2, 8, 20, 0.5), 0 0 20px rgba(34, 211, 238, 0.08)',
  color: '#eef6fa',
};

const microLabelSx = {
  fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
  fontSize: 9.5,
  letterSpacing: '0.14em',
  textTransform: 'uppercase' as const,
  color: 'rgba(148, 197, 222, 0.8)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#eef6fa',
    fontSize: 12,
    bgcolor: 'rgba(34, 211, 238, 0.06)',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34, 211, 238, 0.22)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34, 211, 238, 0.45)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#22d3ee' },
  },
  '& .MuiInputBase-input::placeholder': { color: 'rgba(196, 220, 233, 0.45)', opacity: 1 },
  '& .MuiChip-root': {
    height: 20,
    fontSize: 10,
    fontWeight: 700,
    color: '#a5f3fc',
    bgcolor: 'rgba(34, 211, 238, 0.14)',
    border: '1px solid rgba(34, 211, 238, 0.3)',
  },
};

const formatThousandsInput = (value: string): string => {
  if (!value) return '';
  const [whole, fraction] = value.split('.');
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fraction != null ? `${withCommas}.${fraction}` : withCommas;
};

const sanitizeDecimal = (value: string) => value.replace(/,/g, '').replace(/[^\d.]/g, '');

interface MapResearchCriteriaPanelProps {
  filters: MasterToolSearchFilters;
  onFiltersChange: (next: MasterToolSearchFilters) => void;
  loading: boolean;
  criteriaActive: boolean;
  matchedProjectCount: number;
  matchedUnitCount: number;
  onSearch: () => void;
  onClear: () => void;
}

const RangePair: React.FC<{
  label: string;
  min: string;
  max: string;
  onMin: (v: string) => void;
  onMax: (v: string) => void;
  thousands?: boolean;
  required?: boolean;
  onEnter: () => void;
}> = ({ label, min, max, onMin, onMax, thousands, required, onEnter }) => (
  <Box>
    <Typography sx={{ ...microLabelSx, mb: 0.4 }}>
      {label}
      {required ? (
        <Box component="span" sx={{ color: '#fbbf24', ml: 0.35 }}>
          *
        </Box>
      ) : null}
    </Typography>
    <Box sx={{ display: 'flex', gap: 0.6 }}>
      <TextField
        size="small"
        placeholder="Min"
        value={thousands ? formatThousandsInput(min) : min}
        onChange={(e) => onMin(thousands ? sanitizeDecimal(e.target.value) : e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onEnter();
          }
        }}
        inputProps={{ inputMode: thousands ? 'decimal' : 'text', autoComplete: 'off' }}
        sx={{ flex: 1, ...fieldSx }}
      />
      <TextField
        size="small"
        placeholder="Max"
        value={thousands ? formatThousandsInput(max) : max}
        onChange={(e) => onMax(thousands ? sanitizeDecimal(e.target.value) : e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onEnter();
          }
        }}
        inputProps={{ inputMode: thousands ? 'decimal' : 'text', autoComplete: 'off' }}
        sx={{ flex: 1, ...fieldSx }}
      />
    </Box>
  </Box>
);

const MapResearchCriteriaPanel: React.FC<MapResearchCriteriaPanelProps> = ({
  filters,
  onFiltersChange,
  loading,
  criteriaActive,
  matchedProjectCount,
  matchedUnitCount,
  onSearch,
  onClear,
}) => {
  const [districtOptions, setDistrictOptions] = useState<DistrictOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<ProjectCategoryOption[]>([]);
  const [locationInput, setLocationInput] = useState(filters.locationLabel || '');
  const [locationOptions, setLocationOptions] = useState<OneMapSearchResult[]>([]);
  const [locationSearching, setLocationSearching] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<OneMapSearchResult | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [districts, categories] = await Promise.all([api.getDistricts(), api.getProjectCategories()]);
        setDistrictOptions(districts);
        setCategoryOptions(
          categories.filter((c) => {
            const name = (c.name ?? '').trim().toLowerCase();
            return name === '' || !EXCLUDED_CATEGORIES.has(name);
          }),
        );
      } catch (err) {
        console.error('Failed to load map research criteria options:', err);
      }
    })();
  }, []);

  useEffect(() => {
    const query = locationInput.trim();
    if (query.length < 3) {
      setLocationOptions([]);
      setLocationSearching(false);
      return;
    }
    let cancelled = false;
    setLocationSearching(true);
    const timer = window.setTimeout(() => {
      void searchOnemapLocations(query)
        .then((results) => {
          if (!cancelled) setLocationOptions(results);
        })
        .finally(() => {
          if (!cancelled) setLocationSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [locationInput]);

  const categorySelections = useMemo(
    () =>
      (filters.projectCategories || [])
        .map((id) => categoryOptions.find((c) => c.id === id))
        .filter((c): c is ProjectCategoryOption => Boolean(c)),
    [filters.projectCategories, categoryOptions],
  );

  const patch = useCallback(
    (partial: Partial<MasterToolSearchFilters>) => onFiltersChange({ ...filters, ...partial }),
    [filters, onFiltersChange],
  );

  const clearLocation = useCallback(() => {
    setSelectedLocation(null);
    setLocationInput('');
    const { locationLat, locationLng, locationLabel, radiusKm, ...rest } = filters;
    onFiltersChange(rest);
  }, [filters, onFiltersChange]);

  const radiusMissing =
    Boolean((filters.locationLat ?? '').trim() && (filters.locationLng ?? '').trim()) &&
    !(Number.isFinite(Number(filters.radiusKm ?? '')) && Number(filters.radiusKm ?? '') > 0);

  return (
    <Box sx={{ overflow: 'hidden', color: '#eef6fa' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 0.25,
          pb: 1,
        }}
      >
        <TuneIcon sx={{ fontSize: 16, color: '#22d3ee' }} />
        <Typography sx={{ ...microLabelSx, color: '#22d3ee', flex: 1 }}>Unit Criteria</Typography>
        {criteriaActive && (
          <Chip
            size="small"
            label={`${matchedUnitCount} units · ${matchedProjectCount} proj`}
            sx={{
              height: 20,
              fontSize: 10,
              fontWeight: 800,
              color: '#04121d',
              bgcolor: '#22d3ee',
            }}
          />
        )}
      </Box>

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!loading && !radiusMissing) onSearch();
        }}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 0.9,
            maxHeight: { xs: 280, sm: 360 },
            overflowY: 'auto',
            pr: 0.25,
          }}
        >
            <RangePair
              label="Price ($)"
              min={filters.priceMin || ''}
              max={filters.priceMax || ''}
              onMin={(v) => patch({ priceMin: v })}
              onMax={(v) => patch({ priceMax: v })}
              thousands
              required
              onEnter={onSearch}
            />
            <RangePair
              label="PSF ($/sqft)"
              min={filters.psfMin || ''}
              max={filters.psfMax || ''}
              onMin={(v) => patch({ psfMin: v })}
              onMax={(v) => patch({ psfMax: v })}
              thousands
              onEnter={onSearch}
            />
            <RangePair
              label="Size (sqft)"
              min={filters.sizeMin || ''}
              max={filters.sizeMax || ''}
              onMin={(v) => patch({ sizeMin: v })}
              onMax={(v) => patch({ sizeMax: v })}
              onEnter={onSearch}
            />
            <RangePair
              label="Bedroom"
              min={filters.bedroomMin || ''}
              max={filters.bedroomMax || ''}
              onMin={(v) => patch({ bedroomMin: v })}
              onMax={(v) => patch({ bedroomMax: v })}
              onEnter={onSearch}
            />
            <RangePair
              label="Floor Level"
              min={filters.floorMin || ''}
              max={filters.floorMax || ''}
              onMin={(v) => patch({ floorMin: v })}
              onMax={(v) => patch({ floorMax: v })}
              onEnter={onSearch}
            />
            <RangePair
              label="Bath"
              min={filters.bathMin || ''}
              max={filters.bathMax || ''}
              onMin={(v) => patch({ bathMin: v })}
              onMax={(v) => patch({ bathMax: v })}
              onEnter={onSearch}
            />
            <RangePair
              label="TOP Year"
              min={filters.topYearMin || ''}
              max={filters.topYearMax || ''}
              onMin={(v) => patch({ topYearMin: v })}
              onMax={(v) => patch({ topYearMax: v })}
              onEnter={onSearch}
            />

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography sx={{ ...microLabelSx, mb: 0.4 }}>District</Typography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={districtOptions}
                value={(filters.districts || [])
                  .map((no) => districtOptions.find((d) => d.no === no))
                  .filter((d): d is DistrictOption => Boolean(d))}
                onChange={(_, value) => patch({ districts: value.map((item) => item.no) })}
                getOptionLabel={(opt) => {
                  const name = opt.name ?? opt.generalLocation ?? '';
                  return name || `D${opt.no}`;
                }}
                isOptionEqualToValue={(a, b) => a.no === b.no}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Districts" sx={fieldSx} />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((opt, idx) => {
                    const name = opt.name ?? opt.generalLocation ?? '';
                    return <Chip {...getTagProps({ index: idx })} key={opt.no} label={name || `D${opt.no}`} size="small" />;
                  })
                }
                componentsProps={{ paper: { sx: { ...glassPanelSx, mt: 0.5 } } }}
              />
            </Box>

            <Box>
              <Typography sx={{ ...microLabelSx, mb: 0.4 }}>Region</Typography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={['CCR', 'OCR', 'RCR']}
                value={filters.regions || []}
                onChange={(_, value) => patch({ regions: value })}
                renderInput={(params) => <TextField {...params} placeholder="Regions" sx={fieldSx} />}
                renderTags={(value, getTagProps) =>
                  value.map((opt, idx) => <Chip {...getTagProps({ index: idx })} key={opt} label={opt} size="small" />)
                }
                componentsProps={{ paper: { sx: { ...glassPanelSx, mt: 0.5 } } }}
              />
            </Box>

            <Box>
              <Typography sx={{ ...microLabelSx, mb: 0.4 }}>Tenure</Typography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={['99', '999-year leasehold', 'freehold']}
                value={filters.tenure || []}
                onChange={(_, value) => patch({ tenure: value })}
                getOptionLabel={(opt) => (opt === '99' ? '99-year' : opt === 'freehold' ? 'Freehold' : '999-year')}
                renderInput={(params) => <TextField {...params} placeholder="Tenure" sx={fieldSx} />}
                renderTags={(value, getTagProps) =>
                  value.map((opt, idx) => (
                    <Chip
                      {...getTagProps({ index: idx })}
                      key={opt}
                      label={opt === '99' ? '99-year' : opt === 'freehold' ? 'Freehold' : '999-year'}
                      size="small"
                    />
                  ))
                }
                componentsProps={{ paper: { sx: { ...glassPanelSx, mt: 0.5 } } }}
              />
            </Box>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography sx={{ ...microLabelSx, mb: 0.4 }}>Project Category</Typography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={categoryOptions}
                getOptionLabel={(option) => option.name ?? option.id}
                value={categorySelections}
                onChange={(_, value) => patch({ projectCategories: value.map((item) => item.id) })}
                renderInput={(params) => <TextField {...params} placeholder="Categories" sx={fieldSx} />}
                renderTags={(value, getTagProps) =>
                  value.map((opt, idx) => (
                    <Chip {...getTagProps({ index: idx })} key={opt.id} label={opt.name ?? opt.id} size="small" />
                  ))
                }
                componentsProps={{ paper: { sx: { ...glassPanelSx, mt: 0.5 } } }}
              />
            </Box>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography sx={{ ...microLabelSx, mb: 0.4 }}>Reference Location</Typography>
              <Autocomplete
                size="small"
                options={locationOptions}
                value={selectedLocation}
                inputValue={locationInput}
                loading={locationSearching}
                filterOptions={(x) => x}
                getOptionLabel={(opt) => formatOnemapLocationLabel(opt)}
                isOptionEqualToValue={(a, b) =>
                  a.SEARCHVAL === b.SEARCHVAL && a.LATITUDE === b.LATITUDE && a.LONGITUDE === b.LONGITUDE
                }
                onInputChange={(_, value, reason) => {
                  if (reason !== 'input') return;
                  setLocationInput(value);
                  if (!value.trim()) clearLocation();
                }}
                onChange={(_, value) => {
                  if (!value) {
                    clearLocation();
                    return;
                  }
                  const coords = parseOnemapCoordinates(value);
                  if (!coords) return;
                  setSelectedLocation(value);
                  setLocationInput(formatOnemapLocationLabel(value));
                  patch({
                    locationLat: String(coords.lat),
                    locationLng: String(coords.lng),
                    locationLabel: formatOnemapLocationLabel(value),
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Address, MRT, postal…"
                    sx={fieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {locationSearching ? <CircularProgress size={14} sx={{ color: '#22d3ee' }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                componentsProps={{ paper: { sx: { ...glassPanelSx, mt: 0.5 } } }}
              />
            </Box>

            <Box>
              <Typography sx={{ ...microLabelSx, mb: 0.4 }}>Radius (km)</Typography>
              <TextField
                size="small"
                fullWidth
                disabled={!selectedLocation && !filters.locationLat}
                placeholder="e.g. 5"
                value={filters.radiusKm || ''}
                onChange={(e) => patch({ radiusKm: e.target.value.replace(/[^\d.]/g, '') })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onSearch();
                  }
                }}
                inputProps={{ inputMode: 'decimal', autoComplete: 'off' }}
                sx={{
                  ...fieldSx,
                  ...(radiusMissing
                    ? {
                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#fbbf24',
                          borderWidth: 2,
                        },
                      }
                    : {}),
                }}
              />
            </Box>
          </Box>

          <Typography sx={{ fontSize: 10, color: 'rgba(251, 191, 36, 0.85)', fontWeight: 600, lineHeight: 1.35 }}>
            * EC searches use NPS price. Press Enter or Search to scan matching units.
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
            <Button
              type="submit"
              size="small"
              startIcon={loading ? <CircularProgress size={14} sx={{ color: '#04121d' }} /> : <SearchIcon sx={{ fontSize: 16 }} />}
              disabled={loading || radiusMissing}
              sx={{
                flex: 1,
                fontWeight: 800,
                fontSize: 11.5,
                letterSpacing: '0.06em',
                color: '#04121d',
                borderRadius: '9px',
                background: 'linear-gradient(90deg, #22d3ee 0%, #60a5fa 100%)',
                boxShadow: '0 4px 14px rgba(34, 211, 238, 0.3)',
                '&:hover': { background: 'linear-gradient(90deg, #67e8f9 0%, #93c5fd 100%)' },
                '&.Mui-disabled': { color: 'rgba(4,18,29,0.45)', background: 'rgba(34, 211, 238, 0.25)' },
              }}
            >
              {loading ? 'Scanning…' : 'Search'}
            </Button>
            <Tooltip title="Clear criteria and show all projects">
              <Button
                size="small"
                onClick={onClear}
                disabled={loading}
                sx={{
                  fontWeight: 700,
                  fontSize: 11,
                  color: 'rgba(238,246,250,0.8)',
                  border: '1px solid rgba(34, 211, 238, 0.35)',
                  borderRadius: '9px',
                  minWidth: 64,
                }}
              >
                Clear
              </Button>
            </Tooltip>
          </Box>
        </Box>
    </Box>
  );
};

export default MapResearchCriteriaPanel;
